/**
 * STAIR STUDIO stage 3 (2026-10-09) — SPIRAL and HELICAL stairs (docs/bim-plan/30-stair-studio.md;
 * Matt: spiral + helical in scope from the start). A curved stair lays out through the SAME
 * `stairLayoutForPath` every consumer already calls, returning the same StairLayout shape —
 * treads (as pie / annular-sector polygons), riser lines (radial), climbing side lines (arc
 * chords), the outline (for picking), the walk line (`usedPts`, for the plan arrow) — plus a
 * `curve` record the 3D builder reads. It has no straight legs (`legs` is empty).
 *
 * Anchor: the stair's path. Node 0 is the middle of the FIRST riser; node 0 → node 1 is the
 * direction you start walking. The stair turns to the LEFT (counter-clockwise from above) or
 * the RIGHT (`curveHand`); the center sits to that side of the first riser.
 *
 * Spiral (center column): outer diameter `spiralDiameterFt` (5'), column `spiralColumnIn`
 * (4-1/2" pipe), tread angle `spiralTreadDeg` (30°, twelve to the turn), risers to 9-1/2"
 * (the spiral code limit) unless the stair sets its own count.
 * Helical (open center): inner radius `helixInnerRadiusFt` (blank = twice the width — the
 * code's minimum), the stair's own width and tread depth measured on the walk line 12" from
 * the inner edge.
 */
import type { Path2D, Pt2 } from './types'
import { stairRun, type StairRun } from './stair'
import type { StairLayout, StairLayoutOpts } from './stairLayout'

export type StairShape = 'path' | 'spiral' | 'helical'

export interface StairCurve {
  kind: 'spiral' | 'helical'
  center: Pt2
  rIn: number
  rOut: number
  /** the walk line radius (12" out from the inner edge) */
  rWalk: number
  theta0: number
  /** tread angle (radians, positive) */
  dTheta: number
  /** +1 turns left (counter-clockwise from above), −1 right */
  hand: 1 | -1
  /** column radius (spiral) */
  rColumn: number
}

export function stairShapeOf(params: Record<string, unknown> | undefined): StairShape {
  const v = params?.stairShape
  return v === 'spiral' || v === 'helical' ? v : 'path'
}

const ARC_SEG = Math.PI / 24

/** the curved layout, or null when the path can't anchor it */
export function stairCurveLayout(path: Path2D, opts: StairLayoutOpts, params: Record<string, unknown>): (StairLayout & { curve: StairCurve }) | null {
  const kind = stairShapeOf(params)
  if (kind === 'path' || !path?.nodes || path.nodes.length < 2) return null
  const A = path.nodes[0].pt, B = path.nodes[1].pt
  const L = Math.hypot(B[0] - A[0], B[1] - A[1])
  if (L < 1e-6) return null
  const u: Pt2 = [(B[0] - A[0]) / L, (B[1] - A[1]) / L]
  const n: Pt2 = [-u[1], u[0]]
  const hand: 1 | -1 = params.curveHand === 'right' ? -1 : 1
  const num = (v: unknown, d: number) => { const x = Number(v); return Number.isFinite(x) && x > 0 && v !== null ? x : d }

  let rIn: number, rOut: number, dTheta: number, rColumn = 0
  let calc: StairRun
  const ownCount = Math.round(Number(params.desiredRisers)) >= 1
  if (kind === 'spiral') {
    const R = num(params.spiralDiameterFt, 5) / 2
    rColumn = num(params.spiralColumnIn, 4.5) / 24
    rIn = rColumn
    rOut = Math.max(R, rIn + 1.5)
    dTheta = (num(params.spiralTreadDeg, 30) * Math.PI) / 180
    // the spiral code limit is a 9-1/2" riser
    calc = stairRun(opts.floorToFloorFt, ownCount ? opts.maxRiserFt : Math.max(opts.maxRiserFt, 9.5 / 12), 0)
  } else {
    const w = opts.widthFt
    rIn = num(params.helixInnerRadiusFt, Math.max(2 * w, 3))
    rOut = rIn + w
    calc = stairRun(opts.floorToFloorFt, opts.maxRiserFt, 0)
    dTheta = opts.treadDepthFt / (rIn + 1)
  }
  const rWalk = Math.min(rIn + 1, (rIn + rOut) / 2 + 0.5)
  const rMid = (rIn + rOut) / 2
  const center: Pt2 = [A[0] + hand * n[0] * rMid, A[1] + hand * n[1] * rMid]
  const theta0 = Math.atan2(A[1] - center[1], A[0] - center[0])
  const at = (r: number, th: number): Pt2 => [center[0] + r * Math.cos(th), center[1] + r * Math.sin(th)]
  const ang = (k: number) => theta0 + hand * k * dTheta
  const arc = (r: number, a0: number, a1: number): Pt2[] => {
    const nSeg = Math.max(1, Math.ceil(Math.abs(a1 - a0) / ARC_SEG))
    const out: Pt2[] = []
    for (let i = 0; i <= nSeg; i++) out.push(at(r, a0 + ((a1 - a0) * i) / nSeg))
    return out
  }
  const rr = calc.riserFt
  const N = calc.treads
  const treads: StairLayout['treads'] = []
  const riserLines: StairLayout['riserLines'] = []
  const sideLines: StairLayout['sideLines'] = []
  for (let k = 0; k < N; k++) {
    const a0 = ang(k), a1 = ang(k + 1)
    const outer = arc(rOut, a0, a1), inner = arc(rIn, a1, a0)
    treads.push({ i: k, quad: [...outer, ...inner], zTop: (k + 1) * rr })
    // the climbing side lines: each tread's outer and inner arc, as chords
    for (const ring of [outer, arc(rIn, a0, a1)]) {
      for (let i = 0; i + 1 < ring.length; i++) sideLines.push({ pts: [ring[i], ring[i + 1]], z0: (k + 1) * rr, z1: (k + 1) * rr })
    }
  }
  for (let k = 0; k <= N; k++) {
    const a = ang(k)
    riserLines.push({ pts: [at(rIn, a), at(rOut, a)], z: k * rr, zTop: (k + 1) * rr })
  }
  const aEnd = ang(N)
  const outline = [...arc(rOut, theta0, aEnd), ...arc(rIn, aEnd, theta0)]
  const usedPts = arc(rWalk, theta0, aEnd)
  const runFt = rWalk * dTheta * N
  return {
    calc: { ...calc, runFt },
    legs: [],
    treads,
    landings: [],
    riserLines,
    sideLines,
    outline,
    edgeChains: null,
    usedPts,
    complete: true,
    curve: { kind, center, rIn, rOut, rWalk, theta0, dTheta, hand, rColumn },
  }
}
